// Pure effect-theme derivation. No React, no DOM — type-checked + eyeball-
// verified via /preview-engine across all skins (apps/display has no test runner).
import type React from 'react';
import type { Theme, ThemeKey } from '../channels/scoreboard/themes';
import type { GameSnapshot } from '../socket';

export interface EffectTheme {
  mode: 'dark' | 'light';
  surface: string;
  surfaceBorder: string;
  ink: string;
  sub: string;
  accent: string;
  positive: string;
  alert: string;
  celebrate: string;
  home: string;
  away: string;
  particles: string[];
  glow: number; // 0..1
}

// Relative luminance of a #rrggbb / #rgb / rgba() string. Falls back to dark.
export function luminance(color: string): number {
  const m = color.replace('#', '');
  let r = 0, g = 0, b = 0;
  if (/^[0-9a-f]{6}$/i.test(m)) {
    r = parseInt(m.slice(0, 2), 16); g = parseInt(m.slice(2, 4), 16); b = parseInt(m.slice(4, 6), 16);
  } else if (/^[0-9a-f]{8}$/i.test(m)) {
    r = parseInt(m.slice(0, 2), 16); g = parseInt(m.slice(2, 4), 16); b = parseInt(m.slice(4, 6), 16); // alpha ignored
  } else if (/^[0-9a-f]{3}$/i.test(m)) {
    r = parseInt(m[0] + m[0], 16); g = parseInt(m[1] + m[1], 16); b = parseInt(m[2] + m[2], 16);
  } else {
    const rgb = color.match(/\d+/g);
    if (rgb && rgb.length >= 3) { r = +rgb[0]; g = +rgb[1]; b = +rgb[2]; }
  }
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

// Signature-skin overrides. Everything unset is auto-derived.
export const EFFECT_THEME_OVERRIDES: Partial<Record<ThemeKey, Partial<EffectTheme>>> = {
  'retro-led':   { accent: '#ffb000', positive: '#39ff14', alert: '#ff2d2d', celebrate: '#ffb000', particles: ['#ffb000', '#39ff14', '#ff2d2d'] },
  volt:          { accent: '#c6ff00', positive: '#c6ff00', celebrate: '#c6ff00', particles: ['#c6ff00', '#ffffff', '#ff5a3c'] },
  'stadium-gold':{ accent: '#d4af37', celebrate: '#f0e4b8', particles: ['#d4af37', '#f0e4b8', '#ffffff'] },
  frost:         { accent: '#0ea5e9', positive: '#34d399', alert: '#ef4444', particles: ['#0ea5e9', '#34d399', '#ffffff'] },
  carbon:        { accent: '#ff6a00', positive: '#22c55e', particles: ['#ff6a00', '#9aa3b0', '#22c55e'] },
  broadcast:     { accent: '#e8c87a', positive: '#22c55e', particles: ['#e8c87a', '#9fb0c8', '#ffffff'] },
};

export function deriveEffectTheme(
  theme: Theme,
  themeKey: ThemeKey,
  snapshot: GameSnapshot | null,
): EffectTheme {
  const mode: 'dark' | 'light' = luminance(theme.bg) < 0.5 ? 'dark' : 'light';
  const light = mode === 'light';

  const base: EffectTheme = {
    mode,
    surface: light ? 'rgba(255,255,255,0.92)' : 'rgba(8,12,20,0.78)',
    surfaceBorder: light ? 'rgba(20,30,55,0.18)' : 'rgba(255,255,255,0.22)',
    ink: light ? '#101622' : '#ffffff',
    sub: theme.sub,
    accent: theme.ac,
    positive: light ? '#15a34a' : '#22c55e',
    alert: light ? '#dc2626' : '#ff5a4d',
    celebrate: light ? '#e0a000' : '#fbbf24',
    // colorPrimary is string | null — fall back to theme.ac / fixed away blue
    home: snapshot?.rosters?.home?.colorPrimary ?? theme.ac,
    away: snapshot?.rosters?.away?.colorPrimary ?? '#3a6df0',
    particles: [],
    glow: light ? 0.2 : 1.0,
  };
  const overrides = EFFECT_THEME_OVERRIDES[themeKey] ?? {};
  const merged: EffectTheme = { ...base, ...overrides };
  if (!overrides.particles) {
    merged.particles = [merged.celebrate, merged.accent, merged.positive];
  }
  return merged;
}

// CSS custom properties for the overlay root — consumed by DOM effects.
export function fxVars(t: EffectTheme): React.CSSProperties {
  return {
    ['--fx-surface' as string]: t.surface,
    ['--fx-surface-border' as string]: t.surfaceBorder,
    ['--fx-ink' as string]: t.ink,
    ['--fx-sub' as string]: t.sub,
    ['--fx-accent' as string]: t.accent,
    ['--fx-positive' as string]: t.positive,
    ['--fx-alert' as string]: t.alert,
    ['--fx-celebrate' as string]: t.celebrate,
    ['--fx-home' as string]: t.home,
    ['--fx-away' as string]: t.away,
    ['--fx-glow' as string]: String(t.glow),
  } as React.CSSProperties;
}

// Glow shadow scaled by the skin's glow factor. Use in text-shadow/box-shadow.
export function glowShadow(color: string): string {
  return `0 0 calc(22px * var(--fx-glow)) ${color}`;
}
